'use strict';

const { getDependencyClass } = require('../../lib/dependencies.js');
const performanceMeasure = require('../../lib/performanceMeasure.js');
let measure = performanceMeasure('webcomponents:videoFloat');

const DEBUG = false;

function debug() {
    if (DEBUG) {
        // eslint-disable-next-line no-console
        console.log.apply(null, arguments);
    }
}

getDependencyClass('phoenix-page').then((pageClass) => {
    const FIXED_BOTTOM_AD_SELECTOR = '.m-fixedbottom-ad';

    /**
     * PhoenixVideoFloatManager manages the <phoenix-video-float> elements on the page.
     *
     * A page can have multiple <phoenix-video-float> elements, and only one can be floating at a time.
     */
    class PhoenixVideoFloatManager {
        constructor() {
            // Handle to the video we believe to be actively playing.
            // Element of type PhoenixVideoFloat.
            this.activeVideo = undefined;

            // "float" state of activeVideo
            this.isFloating = false;
        }

        /**
         * hasFixedBottomAd wait for DOMContentLoaded, and check if the page has a fixed bottom ad.
         *
         * @returns {Promise} - Promise of the boolean.
         */
        hasFixedBottomAd() {
            return (
                this._hasFixedBottomAd ||
                (this._hasFixedBottomAd = pageClass.util.whenDomContentLoaded().then(() => {
                    return !!document.querySelector(FIXED_BOTTOM_AD_SELECTOR);
                }))
            );
        }

        /**
         * Called by a <phoenix-video-float> element when becoming visible.
         *
         * @param {PhoenixVideoFloat} video
         */
        onVideoChangeToVisible(video) {
            if (this.activeVideo === video) {
                if (this.isFloating) {
                    this.isFloating = false;
                    video.unfloat();
                }
            }
        }

        /**
         * Called by a <phoenix-video-float> element when becoming not visible.
         *
         * @param {PhoenixVideoFloat} video
         */
        onVideoChangeToNotVisible(video) {
            if (this.activeVideo === video) {
                if (!this.isFloating && video.isPlaying) {
                    this.isFloating = true;
                    video.float();
                }
            }
        }

        /**
         * Clears an existing active video, including removing it's "float" status.
         */
        unsetActiveVideo() {
            if (this.activeVideo) {
                this.activeVideo.unfloat();
                this.isFloating = false;
                this.activeVideo = undefined;
            }
        }

        /**
         * Makes a video the "active" video, and will clear any previously active video.
         */
        setActiveVideo(video) {
            if (this.activeVideo) {
                if (video === this.activeVideo) {
                    return;
                }
                this.unsetActiveVideo();
            }
            this.activeVideo = video;
        }

        pauseHandler(video, didStop) {
            // specifically we want to only unset the active
            // player when the video ends naturally. if the user
            // just pauses the video but resumes scrolling,
            // it shouldn't unfloat.
            if (didStop && !video.isPlaylistVideo) {
                // for the case where we are playing a playlist,
                // we are _not_ using this event to trigger unsetting,
                // the active video, since the end of one video is
                // shortly followed by the start of another.
                this.unsetActiveVideo();
            }
        }
    }

    let _phoenixVideoFloatManager;
    function getPhoenixVideoFloatManager() {
        return _phoenixVideoFloatManager || (_phoenixVideoFloatManager = new PhoenixVideoFloatManager());
    }

    /**
     * <phoenix-video-float> Web Component.
     *
     * Currently, this Web Component is designed to work with the A&E player, which uses an iframe.
     * This iframe should remain an implementation detail of this element, allowing future integrations
     * with other players without changing the manager.
     *
     * @example
     *  <phoenix-video-float class="m-video-player">
     *    <div class="m-video-player--frame-container">
     *      <button type="button" class="m-video-player--close"></button>
     *        <iframe ...></iframe>
     *    </div>
     *  </phoenix-video-float>
     *
     */
    class PhoenixVideoFloat extends pageClass.PhoenixElement {
        constructor() {
            super();

            // Starts with false.
            this.isPlaying = false;

            // the A&E player iframe
            this._videoIframe = undefined;
        }

        connectedCallback() {
            super.connectedCallback(); // set up broadcasted event listeners

            // Float&Park is enabled only on size C/D, return ASAP, before instantiating the manager.
            if (!['C', 'D'].includes(pageClass.util.getCurrentBreakpoint())) {
                return;
            }

            // gets/instantiates the manager
            this.manager = getPhoenixVideoFloatManager();

            // If this page has a fixed bottom ad, apply the corresponding CSS class.
            this.manager.hasFixedBottomAd().then((hasFixedBottomAd) => {
                if (hasFixedBottomAd) {
                    this.classList.add('has-fixed-bottom-ad');
                }
            });

            // This bit of code is here to support a non-JWPlayer library that is used
            // on A&E's sites (history.com, biography.com).
            // Find the iframe of the video player.
            this.waitForElement('phoenix-iframe iframe').then((iframe) => {
                this._videoIframe = iframe;
                // XXX This is a hack around the broken handsake.
                // The iframe is supposed to send 'video:ready', which the page reply to with 'phoenixReady'.
                // This apparently doesn't work. The strategy here is to keep sending phoenixReady until the
                // page receive the first message from the iframe.
                this._phoenixReadyIntervalId = setInterval(() => {
                    this._postMessage('phoenixReady');
                }, 500);
            });

            this.observeVisibilityChanges();

            window.addEventListener(
                'message',
                (e) => {
                    this._handleMessage(e);
                },
                false
            );
        }

        /**
         * isPlaylistVideo tests whether the iframe serving the video content running a playlist
         * versus a single video. We support the presence of a `data-playlist` attribute for this.
         * @returns {boolean}
         */
        get isPlaylistVideo() {
            return this._videoIframe.getAttribute('data-playlist') !== null;
        }

        onChangeToVisible() {
            this.manager.onVideoChangeToVisible(this);
        }

        onChangeToNotVisible() {
            this.manager.onVideoChangeToNotVisible(this);
        }

        /**
         * Make the video float.
         */
        float() {
            this.classList.add('mm-video-player--floating');
        }

        /**
         * Make the video unfloat.
         */
        unfloat() {
            this.classList.remove('mm-video-player--floating');
        }

        /**
         * _postMessage sends a message to the _videoIframe.
         */
        _postMessage(msg) {
            debug('POST MESSAGE: ', msg);
            const videoWindow = this._videoIframe.contentWindow;
            videoWindow.postMessage({ message: msg }, '*');
        }

        /**
         * Receives any postMessage events from the video player running in the iframe.
         */
        _handleMessage(e) {
            // handle messages if there is an iframe
            if (!this._videoIframe) {
                return;
            }
            // handle messages for the corresponding iframe only
            if (this._videoIframe.contentWindow !== e.source) {
                return;
            }
            // handle messages with the right prefix
            if (!(e.data && e.data.message && e.data.message.startsWith('video:'))) {
                return;
            }

            // strip the 'video:' prefix
            let msg = e.data.message.substr(6);
            debug('GOT MESSAGE: ', msg);

            // XXX This is a hack around the broken handsake. See the explanation in connectedCallback.
            if (this._phoenixReadyIntervalId) {
                clearInterval(this._phoenixReadyIntervalId);
                this._phoenixReadyIntervalId = undefined;
            }

            if (msg === 'playing') {
                this.isPlaying = true;
                this.manager.setActiveVideo(this);
            } else if (msg === 'ready') {
                // handshake
                // XXX This is actually not working, since looking at the code of the A&E player,
                // 'video:ready' is not sent if phoenixReady was not received first.
                this._postMessage('phoenixReady');
            } else if (msg === 'paused') {
                this.isPlaying = false;
                // XXX looks like we receive 'video:playing' instead.
                this.manager.pauseHandler(this);
            } else if (msg === 'ended') {
                this.isPlaying = false;
                this.manager.pauseHandler(this, true);
            }
        }
    }
    window.customElements.define('phoenix-video-float', PhoenixVideoFloat);
    measure.end();
});
